/*
 * Agentic loop: OpenAI-compatible chat completions with tool calling.
 * Works against Alibaba Qwen and Xiaomi MiMo (both OpenAI-compatible).
 */

import { getProvider } from "./providers.js";
import { toolSchemas, runTool, isMutating } from "../excel/tools.js";
import { getContextLimit, truncateHistory, truncateToolResults } from "./tokens.js";

const SYSTEM_PROMPT = `You are IMV Sheet, an assistant embedded inside Microsoft Excel.
You can inspect and edit the user's currently open workbook through tools.

Guidelines:
- Always call list_sheets first if you do not yet know the workbook layout.
- Use list_tables to discover structured data before reading ranges.
- Use named ranges for reusable references to important data blocks.
- Read the relevant ranges before answering questions or making edits; never invent cell values.
- Prefer Excel formulas over hard-coded numbers when building calculations, and keep existing formula dependencies intact.
- Make the smallest edit that satisfies the request. Explain what you changed and where (sheet + A1 range).
- When writing, match the shape of the target range exactly.
- Use conditional formatting to highlight important patterns (negatives, outliers, trends).
- Use data validation to create dropdown lists and enforce input rules.
- Use charts to visualize data — pick chartType based on the data shape (Line for trends, Column for comparisons, Pie for proportions, XYScatter for correlations).
- Use sorting and filtering to organize and subset data.
- Be concise. Use the user's language.`;

/**
 * Run one user turn through the model + tool loop.
 *
 * @param {object} opts
 * @param {object} opts.config            { providerId, apiKey, baseUrl, model }
 * @param {Array}  opts.history           prior messages (OpenAI format)
 * @param {string} opts.userMessage       the new user message
 * @param {(text:string)=>void} opts.onAssistantText  called with assistant prose
 * @param {(text:string)=>void} opts.onTextChunk  called with streamed text chunks
 * @param {(name:string,args:object)=>void} opts.onToolStart
 * @param {(name:string,result:object)=>void} opts.onToolResult
 * @param {(name:string,args:object)=>Promise<boolean>} [opts.confirmMutation]
 *        Optional. If provided, called before each mutating tool; return true to
 *        allow it, false to skip. If omitted, mutating tools run directly and the
 *        user relies on Excel's undo (Ctrl+Z) to revert — the Claude-for-Excel UX.
 * @param {AbortSignal} opts.signal       AbortSignal for cancellation
 * @returns {Promise<Array>} the updated message history
 */
export async function runAgent(opts) {
  const { config, history, userMessage, onAssistantText, onTextChunk, onToolStart, onToolResult, confirmMutation, signal } = opts;
  const provider = getProvider(config.providerId);

  // Truncate history to fit context window
  const contextLimit = getContextLimit(config.model);
  const maxTokens = Math.floor(contextLimit * 0.8); // Reserve 20% for response
  let truncatedHistory = truncateToolResults(history, 10); // Keep last 10 messages intact
  truncatedHistory = await truncateHistory(truncatedHistory, maxTokens);

  const messages = [
    { role: "system", content: SYSTEM_PROMPT },
    ...truncatedHistory,
    { role: "user", content: userMessage },
  ];

  const maxIterations = 12;
  for (let i = 0; i < maxIterations; i++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const resp = await streamChatCompletion(provider, config, messages, signal, onTextChunk);
    const choice = resp.choices && resp.choices[0];
    if (!choice) throw new Error("Empty response from model.");
    const msg = choice.message;
    messages.push(msg);

    if (msg.content && onAssistantText) onAssistantText(msg.content);

    const toolCalls = msg.tool_calls || [];
    if (toolCalls.length === 0) break; // model is done

    for (const call of toolCalls) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      const name = call.function.name;
      let args = {};
      try {
        args = call.function.arguments ? JSON.parse(call.function.arguments) : {};
      } catch (e) {
        args = {};
      }
      if (onToolStart) onToolStart(name, args);

      let result;
      if (isMutating(name) && confirmMutation) {
        const allowed = await confirmMutation(name, args);
        result = allowed ? await runTool(name, args) : { ok: false, error: "Skipped by user." };
      } else {
        result = await runTool(name, args);
      }

      if (onToolResult) onToolResult(name, result);
      messages.push({
        role: "tool",
        tool_call_id: call.id,
        content: JSON.stringify(result),
      });
    }
  }

  // strip the system message before returning history
  return messages.filter((m) => m.role !== "system");
}

/**
 * Stream chat completion with SSE parsing.
 * Includes retry logic with exponential backoff for transient errors.
 */
async function streamChatCompletion(provider, config, messages, signal, onTextChunk) {
  const maxRetries = 3;
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await doStreamRequest(provider, config, messages, signal, onTextChunk);
    } catch (e) {
      lastError = e;

      // Don't retry if aborted
      if (e.name === "AbortError" || signal?.aborted) {
        throw e;
      }

      // Don't retry client errors (4xx) except 429 (rate limit)
      if (e.status && e.status >= 400 && e.status < 500 && e.status !== 429) {
        throw e;
      }

      // Don't retry after max attempts
      if (attempt >= maxRetries) {
        throw e;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Check if aborted during wait
      if (signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
    }
  }

  throw lastError || new Error("Unknown error");
}

/**
 * Perform the actual streaming request.
 */
async function doStreamRequest(provider, config, messages, signal, onTextChunk) {
  const baseUrl = (config.baseUrl || provider.directBaseUrl).replace(/\/$/, "");
  const url = `${baseUrl}/chat/completions`;

  const headers = { "Content-Type": "application/json" };
  if (config.authToken) {
    // Auth-gateway mode: the JWT identifies the user; the server holds the
    // provider key and meters token quota. No provider key is sent from here.
    headers["Authorization"] = `Bearer ${config.authToken}`;
  } else if (config.apiKey) {
    headers[provider.authHeader] = (provider.authPrefix || "") + config.apiKey;
  }

  const body = {
    model: config.model || provider.defaultModel,
    messages,
    tools: toolSchemas(),
    tool_choice: "auto",
    temperature: 0.2,
    stream: true,
  };

  let res;
  try {
    res = await fetch(url, { method: "POST", headers, body: JSON.stringify(body), signal });
  } catch (e) {
    // Network error
    if (e.name === "AbortError") {
      throw e;
    }
    const err = new Error(`Network error: ${e.message}`);
    err.code = "NETWORK_ERROR";
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // The auth gateway returns JSON {error: "..."} with a specific reason
    // (expired session, quota exceeded, account disabled). Surface it directly.
    let serverError = "";
    try {
      serverError = JSON.parse(text).error || "";
    } catch (e) {
      /* not JSON */
    }
    let message = `API error ${res.status}`;

    // Provide helpful messages for common errors
    switch (res.status) {
      case 401:
        message += serverError ? `: ${serverError}` : ": Authentication failed. Please sign in again.";
        break;
      case 403:
        message += serverError ? `: ${serverError}` : ": Access forbidden.";
        break;
      case 429:
        message += ": Rate limit exceeded. Please wait a moment and try again.";
        break;
      case 500:
        message += ": Server error. The provider's server is experiencing issues.";
        break;
      case 502:
      case 503:
        message += ": Service temporarily unavailable. Please try again in a few seconds.";
        break;
      case 504:
        message += ": Gateway timeout. The provider's server took too long to respond.";
        break;
      default:
        message += `: ${text.slice(0, 200)}`;
    }

    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let content = "";
  const toolCalls = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.startsWith("data:")) continue;

      const data = trimmed.slice(5).trim();
      if (data === "[DONE]") break;

      try {
        const parsed = JSON.parse(data);
        const delta = parsed.choices?.[0]?.delta;
        if (!delta) continue;

        if (delta.content) {
          content += delta.content;
          if (onTextChunk) onTextChunk(content);
        }

        if (delta.tool_calls) {
          for (const tc of delta.tool_calls) {
            const idx = tc.index;
            if (!toolCalls[idx]) {
              toolCalls[idx] = {
                id: tc.id || "",
                type: "function",
                function: { name: "", arguments: "" },
              };
            }
            if (tc.id) toolCalls[idx].id = tc.id;
            if (tc.function?.name) toolCalls[idx].function.name += tc.function.name;
            if (tc.function?.arguments) {
              toolCalls[idx].function.arguments += tc.function.arguments;
            }
          }
        }
      } catch (e) {
        // Skip malformed chunks
      }
    }
  }

  const message = {
    role: "assistant",
    content: content || null,
    tool_calls: toolCalls.length > 0 ? toolCalls.filter(Boolean) : undefined,
  };

  return { choices: [{ message, finish_reason: "stop" }] };
}
